import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {AlertTriangle} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Label} from '@/components/ui/label'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {Stack} from '@/components/shared/Stack'
import {IdentityLifecycleState} from '@/types/identity'
import type {IdentityListItem} from '@/types/identity'
import {InfoBanner} from '@/components/shared/InfoBanner'

interface IdentityManagerPickerProps {
    /** Currently selected manager Identity id (or null = empty). */
    selectedIdentityId: string | null
    /** Identity displayName of the selected manager — survives a page reload from URL state. */
    selectedPrimaryLabel: string
    /**
     * Party displayName of the selected manager — non-empty only for
     * multi-employment Identities (Identity displayName ≠ Party displayName).
     */
    selectedSecondaryLabel: string
    /**
     * Callback fired on selection / clear. The {@code secondaryLabel} is the
     * Party display name, empty when the Identity displayName already carries
     * the Party name (single-employment).
     */
    onChange: (identityId: string | null, primaryLabel: string, secondaryLabel: string) => void
    disabled?: boolean
}

/**
 * Identity picker for the onboarding wizard's MANAGED_BY edge (epic 986
 * Phase D, spec §7).
 *
 * <p>Behavioural contract:
 * <ul>
 *   <li>Searches active WORKFORCE Identities — matches on Identity displayName
 *       AND Party displayName (the backend's {@code q} filter walks both).</li>
 *   <li>Each result row renders the Identity's own displayName as the primary
 *       label and the Party displayName as the description when the two
 *       differ — this is the multi-employment disambiguation surface.</li>
 *   <li>Empty-result state shows "No identities found" inside the dropdown.</li>
 *   <li>Inactive-manager advisory — if the selected Identity's lifecycle
 *       state is not ACTIVE, an inline advisory banner appears below the
 *       picker. The wizard can still advance (non-blocking).</li>
 * </ul>
 */
export function IdentityManagerPicker({
    selectedIdentityId,
    selectedPrimaryLabel,
    selectedSecondaryLabel,
    onChange,
    disabled,
}: IdentityManagerPickerProps) {
    const i18n = useI18n()
    const services = useServices()
    const [searchQuery, setSearchQuery] = useState('')

    const searchResults = useQuery({
        queryKey: ['onboarding-manager-search', searchQuery],
        queryFn: () => services.identities.list({q: searchQuery, size: 10}),
        enabled: searchQuery.length >= 2,
    })

    /* WHY: when the picker mounts with a prefilled identity, surface its
     * lifecycle state to drive the inactive advisory. The detail call is
     * cheap and cached; skipped when no selection exists. */
    const selectedDetail = useQuery({
        queryKey: ['onboarding-manager-detail', selectedIdentityId],
        queryFn: () => services.identities.detail(selectedIdentityId!),
        enabled: !!selectedIdentityId,
    })

    const isInactive = selectedDetail.data
        && selectedDetail.data.lifecycleState !== IdentityLifecycleState.ACTIVE

    const formatOption = (identity: IdentityListItem): ComboboxOption => {
        const primary = identity.derivedDisplayName ?? identity.party.displayName
        const description = primary !== identity.party.displayName ? identity.party.displayName : ''
        return {
            value: String(identity.id),
            label: primary,
            description,
        }
    }

    const options: ComboboxOption[] = (searchResults.data?.content ?? []).map(formatOption)

    const composedSelectedLabel = selectedSecondaryLabel
        ? i18n.onboarding.organization.managerSecondaryLabel({
            primary: selectedPrimaryLabel,
            secondary: selectedSecondaryLabel,
          })
        : selectedPrimaryLabel

    return (
        <Stack gap="sm" data-testid="identity-manager-picker">
            <Label>{i18n.onboarding.organization.managerLabel}</Label>
            <Combobox
                options={options}
                value={selectedIdentityId}
                onValueChange={(val) => {
                    if (val === null) {
                        onChange(null, '', '')
                        return
                    }
                    const identity = searchResults.data?.content?.find((i) => String(i.id) === val)
                    if (identity) {
                        const primary = identity.derivedDisplayName ?? identity.party.displayName
                        const secondary = primary !== identity.party.displayName ? identity.party.displayName : ''
                        onChange(String(identity.id), primary, secondary)
                    }
                }}
                onSearch={setSearchQuery}
                placeholder={i18n.onboarding.organization.managerSearchPlaceholder}
                emptyMessage={i18n.onboarding.organization.managerEmptyResults}
                isLoading={searchResults.isLoading}
                selectedLabel={composedSelectedLabel}
                disabled={disabled}
                data-testid="combobox-manager-identity"
            />
            {isInactive && (
                <InfoBanner variant="warning" icon={AlertTriangle}>
                    <span data-testid="manager-inactive-advisory">
                        {i18n.onboarding.organization.managerInactiveAdvisory}
                    </span>
                </InfoBanner>
            )}
        </Stack>
    )
}
